import { toast, Toaster } from 'sonner'
import { ToastProps as AppToastProps } from '@/entities/app/interfaces'
import { useRef, useCallback } from 'react'

export interface EnhancedToastProps {
	message?: string
	type: 'info' | 'error' | 'success' | 'warning' | 'progress'
	position?:
		| 'top-right'
		| 'top-center'
		| 'top-left'
		| 'bottom-right'
		| 'bottom-center'
		| 'bottom-left'
	duration?: number
	actionLabel?: string
	onAction?: () => void
	progress?: number
	onComplete?: () => void
	toastId?: string
}

const showToast = ({
	message,
	type = 'info',
	position = 'top-right',
	duration = 4000,
	actionLabel,
	onAction,
	progress = 0,
	onComplete,
	toastId,
}: EnhancedToastProps) => {
	const getTitle = (): string => {
		switch (type) {
			case 'info':
				return 'Información'
			case 'success':
				return 'Éxito'
			case 'error':
				return 'Error'
			case 'warning':
				return 'Advertencia'
			case 'progress':
				return 'Cargando'
			default:
				return 'Notificación'
		}
	}

	const getDescription = (): string => {
		if (message) return message
		switch (type) {
			case 'info':
				return 'Información importante'
			case 'success':
				return 'Operación exitosa'
			case 'error':
				return 'Ocurrió un error'
			case 'warning':
				return 'Atención requerida'
			case 'progress':
				return 'Cargando...'
			default:
				return ''
		}
	}

	const options = {
		duration,
		position,
		action:
			actionLabel && onAction
				? {
						label: actionLabel,
						onClick: onAction,
				  }
				: undefined,
		description: getDescription(),
	}

	if (type === 'progress') {
		const id = toastId || `progress-${Date.now()}`
		const roundedProgress = Math.round(progress || 0)

		if (roundedProgress >= 100) {
			toast.loading(`${getTitle()} (100%)`, {
				description: message || 'Finalizando...',
				id,
				duration: 500,
				position,
			})

			setTimeout(() => {
				toast.success('Éxito', {
					description: 'Completado con éxito',
					id,
					duration: duration || 3000,
					position,
				})

				if (onComplete) {
					setTimeout(() => {
						onComplete()
					}, 300)
				}
			}, 500)

			return id
		}

		toast.loading(`${getTitle()} (${roundedProgress}%)`, {
			description: getDescription(),
			id,
			duration: duration || 0,
			position,
		})

		return id
	}

	switch (type) {
		case 'info':
			return toast.info(getTitle(), options)
		case 'success':
			return toast.success(getTitle(), options)
		case 'error':
			return toast.error(getTitle(), options)
		case 'warning':
			return toast.warning(getTitle(), options)
		default:
			return toast(getTitle(), options)
	}
}

export function useProgressToast() {
	const toastIdRef = useRef<string | null>(null)
	const lastProgressRef = useRef<number>(0)

	const startProgress = useCallback(
		(options: Omit<EnhancedToastProps, 'type' | 'progress'>) => {
			const id = `progress-${Date.now()}`
			toastIdRef.current = id
			lastProgressRef.current = 0

			showToast({
				...options,
				type: 'progress',
				progress: 0,
				toastId: id,
			})

			return id
		},
		[]
	)

	const updateProgress = useCallback(
		(
			progress: number,
			options?: Omit<EnhancedToastProps, 'type' | 'progress' | 'toastId'>
		) => {
			if (!toastIdRef.current) return

			const roundedProgress = Math.round(progress)

			if (Math.abs(roundedProgress - lastProgressRef.current) >= 1) {
				lastProgressRef.current = roundedProgress

				showToast({
					...options,
					type: 'progress',
					progress: roundedProgress,
					toastId: toastIdRef.current,
				})
			}
		},
		[]
	)

	const complete = useCallback(
		(options?: Omit<EnhancedToastProps, 'type' | 'progress' | 'toastId'>) => {
			if (!toastIdRef.current) return

			showToast({
				...options,
				type: 'progress',
				progress: 100,
				toastId: toastIdRef.current,
			})

			toastIdRef.current = null
			lastProgressRef.current = 0
		},
		[]
	)

	const dismiss = useCallback(() => {
		if (toastIdRef.current) {
			toast.dismiss(toastIdRef.current)
			toastIdRef.current = null
			lastProgressRef.current = 0
		}
	}, [])

	const error = useCallback((message?: string) => {
		if (!toastIdRef.current) return

		toast.error(message || 'Ocurrió un error', {
			id: toastIdRef.current,
			duration: 3000,
		})

		toastIdRef.current = null
		lastProgressRef.current = 0
	}, [])

	return {
		startProgress,
		updateProgress,
		complete,
		dismiss,
		error,
		isActive: () => !!toastIdRef.current,
		getCurrentId: () => toastIdRef.current,
	}
}

const Toast = (props: EnhancedToastProps) => {
	showToast(props)
	return null
}

export const ToastContainer = () => (
	<Toaster
		theme='light'
		closeButton
		richColors
		toastOptions={{
			className: 'custom-toast',
			style: {
				fontFamily: 'inherit',
				borderRadius: '6px',
			},
		}}
	/>
)

export const LegacyToast = ({ message, type }: AppToastProps) => {
	showToast({
		message,
		type: type as 'info' | 'error' | 'success' | 'warning',
	})
	return null
}

export default Toast
